import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import api from "../../lib/api";
import { toast } from "sonner";
import { X, UserSwitch, Info } from "@phosphor-icons/react";
import { API } from "../../App";

const REASONS = [
  { key: "current_bde_unable", label: "Current BDE unable to handle" },
  { key: "location_mismatch", label: "Location mismatch" },
  { key: "customer_requested_different", label: "Customer requested different executive" },
  { key: "workload_balancing", label: "Workload balancing" },
  { key: "manager_reassignment", label: "Manager reassignment" },
  { key: "follow_up_not_done", label: "Follow-up not done" },
  { key: "leave_absence", label: "Leave / Absence" },
  { key: "resignation_handover", label: "Resignation handover" },
  { key: "product_category_mismatch", label: "Product category mismatch" },
  { key: "duplicate_assignment", label: "Duplicate assignment" },
  { key: "other", label: "Other" },
];

export default function LeadTransferModal({ open, lead, user, onClose, onTransferred }) {
  const [newOwnerId, setNewOwnerId] = useState("");
  const [reason, setReason] = useState("workload_balancing");
  const [note, setNote] = useState("");
  const [users, setUsers] = useState([]);
  const [submitting, setSubmitting] = useState(false);

  const role = (user?.role || "").toLowerCase();
  const isBde = role === "bde";

  useEffect(() => {
    if (!open) return;
    setNewOwnerId(""); setReason("workload_balancing"); setNote("");
    api.get(`/users`).then((r) => {
      const all = r.data || [];
      const eligible = all.filter((u) => {
        if (u.user_id === (lead?.assigned_to_id || "")) return false;
        if (u.user_id === user?.user_id) return false;
        // BDE may transfer ONLY to another BDE; elevated roles may pick bde or bdm
        if (isBde) return (u.role || "").toLowerCase() === "bde";
        return ["bde", "bdm"].includes((u.role || "").toLowerCase());
      });
      setUsers(eligible);
    }).catch(() => setUsers([]));
  }, [open, lead?.assigned_to_id, isBde, user?.user_id]);

  const submit = async () => {
    if (!newOwnerId) { toast.error("Select new BDE"); return; }
    setSubmitting(true);
    try {
      const { data } = await api.post(`${API}/leads/${lead.lead_id}/transfer`, {
        new_owner_id: newOwnerId,
        reason, note,
        requester_user_id: user?.user_id || "",
        requester_name: user?.name || "",
        requester_role: user?.role || "",
      });
      if (data?.status === "pending") {
        toast.success("Transfer request submitted for approval");
      } else {
        toast.success("Lead transferred");
      }
      onTransferred && onTransferred();
      onClose();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Transfer failed");
    } finally {
      setSubmitting(false);
    }
  };

  const targetLabel = useMemo(() => (isBde ? "New BDE *" : "New BDE / BDM *"), [isBde]);

  if (!open || !lead) return null;
  return (
    <div className="fixed inset-0 z-[60] bg-black/40 flex items-center justify-center p-4" data-testid="lead-transfer-modal">
      <div className="bg-white border border-slate-200 max-w-lg w-full">
        <div className="border-b border-slate-200 px-5 py-3 flex items-center justify-between">
          <div className="flex items-center gap-2">
            <UserSwitch size={20} className="text-[#002FA7]" />
            <h3 className="text-lg font-bold">{isBde ? "Request Lead Transfer" : "Transfer Lead"}</h3>
          </div>
          <button onClick={onClose} data-testid="lead-transfer-close" className="p-1 hover:bg-slate-100"><X size={16} /></button>
        </div>
        <div className="p-5 space-y-3">
          <div className="text-xs text-slate-500">Lead: <span className="font-bold text-slate-800">{lead.name}</span> · Current owner: <span className="font-bold text-slate-800">{lead.assigned_to || "—"}</span></div>
          {isBde && (
            <div className="flex items-start gap-2 bg-amber-50 border border-amber-200 px-3 py-2 text-[11px] text-amber-800" data-testid="lead-transfer-approval-notice">
              <Info size={14} className="mt-0.5 flex-shrink-0" />
              <div>Your request will go to BDM / Manager / Admin for approval. The lead will remain with you until approved.</div>
            </div>
          )}
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">{targetLabel}</label>
            <select data-testid="lead-transfer-new-owner" value={newOwnerId} onChange={(e) => setNewOwnerId(e.target.value)}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 outline-none mt-1">
              <option value="">— Select —</option>
              {users.map((u) => <option key={u.user_id} value={u.user_id}>{u.name} ({(u.role || "").toUpperCase()})</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Reason *</label>
            <select data-testid="lead-transfer-reason" value={reason} onChange={(e) => setReason(e.target.value)}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 outline-none mt-1">
              {REASONS.map((r) => <option key={r.key} value={r.key}>{r.label}</option>)}
            </select>
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-wider font-bold text-slate-600">Note / Remarks</label>
            <textarea data-testid="lead-transfer-note" value={note} onChange={(e) => setNote(e.target.value)} rows={3}
              className="w-full rounded-none border border-slate-300 focus:border-[#002FA7] p-2 outline-none mt-1 text-sm" />
          </div>
        </div>
        <div className="border-t border-slate-200 px-5 py-3 flex justify-end gap-2">
          <button onClick={onClose} className="px-4 py-2 text-xs uppercase tracking-wider font-bold border border-slate-300 hover:bg-slate-50">Cancel</button>
          <button data-testid="lead-transfer-submit" onClick={submit} disabled={submitting || !newOwnerId}
            className="px-4 py-2 text-xs uppercase tracking-wider font-bold bg-[#002FA7] text-white hover:bg-[#00227A] disabled:bg-slate-300">
            {submitting ? (isBde ? "Submitting…" : "Transferring…") : (isBde ? "Submit Request" : "Confirm Transfer")}
          </button>
        </div>
      </div>
    </div>
  );
}
